import React from 'react';

import { GridColumn } from '../../../common/components/Grid/GridDataTypes';
import { Grid } from '../../../common/components/Grid/Grid';
import { GetClientGroupItemDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { useTranslation } from '../../../common/hooks/useTranslation';
import { useAuth } from '../../../common/hooks/useAuth';
import { usePokCore } from '../../../common/hooks/usePokCore';
import { PermissionsEnum } from '../../../common/pokCore/authorization/permissions';
import {
  contactsToText,
  renderContacts,
} from '../Clients/ClientWithContactsSearch';

import { DeleteButton } from './DeleteButton';
import { NewWindowButton } from './NewWindowButton';

interface ClientSimpleSearchProps {
  items: GetClientGroupItemDto[];
  onDeleteClick: (itemId: string) => Promise<void> | void;
  onRowClick: (clientId: string) => void;
}

const groupItemForGrid = (
  item: GetClientGroupItemDto,
  onDeleteClick: (itemId: string) => Promise<void> | void,
  canOpenClient: boolean,
  lang: string,
) => ({
  key: item.id,
  values: {
    'item.systemNumber': item.client?.systemNumber ?? '',
    'item.name': item.client?.name ?? '',
    'item.nip': item.client?.nip ?? '',
    'item.contacts': renderContacts(item.client?.contacts, lang),
    newWindowButton: canOpenClient ? (
      <NewWindowButton clientId={item.client?.id} />
    ) : null,
    deleteButton: <DeleteButton itemId={item.id} onDelete={onDeleteClick} />,
  },
  orderByStringValues: {
    'item.contacts': contactsToText(item.client?.contacts, lang),
  },
  isInactive: false,
});

export const ClientSimpleSearch: React.FC<ClientSimpleSearchProps> = props => {
  const { t, tk } = useTranslation('clientLng');
  const auth = useAuth();
  const pok = usePokCore();
  const canOpenClient = auth.check(PermissionsEnum.Clients);
  const lang = pok.getLanguage();

  const handleRowClick = (itemId: string) => {
    const clientId = props.items.find(item => item.id === itemId)?.client?.id;
    if (clientId) {
      props.onRowClick(clientId);
    }
  };

  const columns: GridColumn[] = [
    {
      header: '',
      property: 'newWindowButton',
      noFilter: true,
      noSorting: true,
    },
    { header: t(tk.common.sysNumber), property: 'item.systemNumber' },
    { header: t(tk.common.name), property: 'item.name' },
    { header: t(tk.client.nip), property: 'item.nip' },
    {
      header: t(tk.client.contactsColumn),
      property: 'item.contacts',
      noSorting: true,
    },
    { header: '', property: 'deleteButton', noFilter: true, noSorting: true },
  ];

  return (
    <Grid
      data={props.items.map(item =>
        groupItemForGrid(item, props.onDeleteClick, canOpenClient, lang),
      )}
      columns={columns}
      showFilter={true}
      initialOrderColumn="item.systemNumber"
      availablePageSizes={[10, 20, 50]}
      emptyGridMessage={t(tk.clientGroup.noClientsInGroup)}
      withFilterInColumns={true}
      onRowClick={handleRowClick}
    />
  );
};
